import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragEndEvent, closestCorners } from '@dnd-kit/core';
import { Shield, Users, Layers, Lock } from 'lucide-react'; // 🛡️ Added for Scope Visuals

// 🎯 Types and Hooks
import { AppState, AppAction, KanbanTask, BoardConfig, TaskStatus, TemplateType, DataScope } from '@/types';
import { useTaskState } from '../features/kanban-engine/hooks/useTaskState';
import { KanbanBoard } from '../features/kanban-engine/components/KanbanBoard';

const DEFAULT_TASKS_BOARD: BoardConfig = {
  id: 'main-tasks-board',
  title: 'My Action Protocols',
  templateType: TemplateType.ACTION,
  allowedStatuses: ['todo', 'in_progress', 'finalized'],
  visibility: 'member',
  isCollapsed: false,
  defaultGroupBy: 'priority',
  activeFilters: []
};

interface TasksProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

/**
 * 🏛️ Tasks View (Hybrid Scoping Edition)
 * Orchestrates task visibility across Personal and Shared boundaries.
 */
export const Tasks: React.FC<TasksProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const currentUserId = state.currentUser?.id || 'guest';
  const currentHouseholdId = state.currentHouseholdId;

  // 🛡️ HYBRID SCOPE STATE: Default to BOTH if in a household, otherwise lock to PERSONAL
  const [activeScope, setActiveScope] = useState<DataScope>(
    currentHouseholdId ? 'BOTH' : 'PERSONAL'
  );

  // 🎯 Filter global tasks to match the selected Scope and TemplateType
  const filteredActionTasks = useMemo(() => {
    return state.tasks.filter(t => {
      const isAction = t.templateType === TemplateType.ACTION;
      
      // 🛡️ SCOPE FIREWALL: Apply logic based on privacy boundary
      const isPersonal = t.scope === 'personal';
      const isShared = t.scope === 'household';

      let scopeMatches = true;
      if (activeScope === 'PERSONAL') scopeMatches = isPersonal;
      if (activeScope === 'SHARED') scopeMatches = isShared;
      // 'BOTH' implies scopeMatches is always true

      // 🔒 PRIVACY GUARD: Never show personal tasks owned by other users
      const ownershipMatch = isPersonal ? t.ownerId === currentUserId : true;

      return isAction && scopeMatches && ownershipMatch;
    });
  }, [state.tasks, activeScope, currentUserId]);

  // 🎯 Hook integration with scope-awareness
  const { tasks, moveTask } = useTaskState(
    filteredActionTasks,
    currentUserId,
    dispatch,
    currentHouseholdId,
    activeScope, // 🛡️ DataScope argument
    state.households.find(h => h.id === currentHouseholdId)?.permissions?.[currentUserId] // 🛡️ Permissions argument
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeTaskId = String(active.id);
    const overDroppableId = String(over.id);
    const [, targetStatus] = overDroppableId.split('-');

    const task = tasks.find(t => t.id === activeTaskId);
    if (targetStatus && targetStatus !== task?.status) {
      // 🛡️ PERMISSION CHECK: Verify user can move household tasks
      if (task?.scope === 'household' && !state.households.find(h => h.id === currentHouseholdId)?.permissions?.[currentUserId]?.canManageTasks) {
        return; 
      }
      moveTask(activeTaskId, targetStatus as TaskStatus);
    }
  };

 const handleKanbanBoardMove = React.useCallback(
    (taskId: string, newStatus: TaskStatus, targetScope: 'personal' | 'household') => {
      // The KanbanBoard provides 'targetScope' as 'personal' or 'household'.
      // Our useTaskState's moveTask expects 'destinationBoard' (BoardConfig | undefined)
      // as its third argument, or no third argument for internal moves.
      // We pass DEFAULT_TASKS_BOARD to satisfy the type.

      const task = tasks.find(t => t.id === taskId);

      // 🛡️ PERMISSION CHECK: Verify user can move household tasks
      if (task?.scope === 'household' && !state.households.find(h => h.id === currentHouseholdId)?.permissions?.[currentUserId]?.canManageTasks) {
        return;
      }

      moveTask(taskId, newStatus, DEFAULT_TASKS_BOARD);
    },
    // Ensure all dependencies are correctly listed for useCallback
    [moveTask, tasks, currentHouseholdId, currentUserId, state.households]
  );

  return (
    <div className="max-w-7xl mx-auto p-6 pb-32 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
            {t('nav.tasks', 'Action Protocol')}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <Lock className="w-3 h-3 text-emerald-500" />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
              {currentHouseholdId ? 'Hybrid Tenancy Active' : 'Personal Partition Mode'}
            </p>
          </div>
        </div>

        {/* 🛡️ SCOPE TOGGLE UI */}
        {currentHouseholdId && (
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-inner">
            {[
              { id: 'PERSONAL', icon: Shield, color: 'text-emerald-500' },
              { id: 'BOTH', icon: Layers, color: 'text-indigo-500' },
              { id: 'SHARED', icon: Users, color: 'text-indigo-600' }
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveScope(s.id as DataScope)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeScope === s.id 
                    ? 'bg-white dark:bg-slate-700 shadow-lg ' + s.color 
                    : 'text-slate-400'
                }`}
              >
                <s.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{s.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <DndContext
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <KanbanBoard
          config={DEFAULT_TASKS_BOARD}
          tasks={tasks}
          onMoveTask={handleKanbanBoardMove}
          currentUserId={currentUserId} 
          currentHouseholdId={currentHouseholdId} 
        />
      </DndContext>
    </div>
  );
};